import _ from "lodash";
import diff from "microdiff";
import Awesomplete from "awesomplete";
/**
 * Initializes WPD point group inputs with metadata tag drop downs.
 * @param {VueComponent} dataEntryVM - DataEntry Vue instance
 */
function init(wpd, wpdDocument, dataEntryVM) {
	class PointGroups {
		// set storing point groups in axis
		static #settingsPopupId = "point-group-settings-popup";
		static #tableId = "point-group-table";
		static supportedAxisTypes = ["xy", "bar"];
		static colorSortKeywords = {
			negative: ["minimum", "lower", "10", "25", "-1"],
			positive: ["+1", "75", "90", "upper", "maximum"],
		};
		static colors = {
			negative: [
				wpd.Colors.negativePointGroupDark,
				wpd.Colors.negativePointGroupMedium,
				wpd.Colors.negativePointGroupLight,
			],
			positive: [
				wpd.Colors.positivePointGroupDark,
				wpd.Colors.positivePointGroupMedium,
				wpd.Colors.positivePointGroupLight,
			],
		};
		static colorsA = {
			negative: [
				wpd.Colors.negativePointGroupDarkA,
				wpd.Colors.negativePointGroupMediumA,
				wpd.Colors.negativePointGroupLightA,
			],
			positive: [
				wpd.Colors.positivePointGroupDarkA,
				wpd.Colors.positivePointGroupMediumA,
				wpd.Colors.positivePointGroupLightA,
			],
		};

		// maximum tolerance when updating point group format for tuple
		// formation; e.g. how many pixels on each side of the opposite axis
		// is allowed for a point to be considered part of a tuple.
		// the maximum value is the limit at which the updateToNewFormat
		// function will attempt to call refreshTuples with, incrementing by
		// one each time and testing for changes in the dataset tuples
		// minimum tolerance is where the tolerance refreshTuples will start
		// at
		//   :           :
		// ● :     —     : ← outside of tolerance
		//   :     |     :
		//   :     |     :
		//   :     |     :
		//   :     |     :
		//   :<--->●<--->: ← tolerance boundaries (tolerance x 2)
		//   :     |     :
		//   :     |     :
		//   :     |     :
		//   :     —  ●  : ← within tolerance
		//   :           :
		static maxTolerance = 5;
		static minTolerance = 2;

		// Fraction of the shared-axis calibration pixel span used as the
		// satellite-to-center proximity tolerance, so it scales with the graph
		// instead of a one-size-fits-all pixel count (too tight on large charts,
		// too loose on small ones). Floored at maxTolerance. (DCTL-1768)
		static proximityToleranceFraction = 0.02;

		#currentAxisDirection;
		#awesompletes = [];
		#cache = {};

		constructor() {
			this.$popup = wpdDocument.getElementById(PointGroups.#settingsPopupId);
			this.$table = wpdDocument.getElementById(PointGroups.#tableId);
			this.$okCancelPopup = wpdDocument.getElementById("okCancelPopup");
			this.$layover = wpdDocument.getElementById("shadow");

			this.tags = dataEntryVM.getTags();
		}

		/**
		 * Checks if the axis has point groups.
		 * @deprecated
		 * @param {object} axisMetadata - Axis metadata
		 * @returns {boolean} - True if the axis has point groups, false otherwise
		 */
		hasPointGroups(axisMetadata) {
			return this.#hasPointGroups(axisMetadata);
		}

		#hasPointGroups(axisMetadata) {
			if (
				axisMetadata &&
				axisMetadata.pointGroups &&
				!wpd.utils.isEmptyObject(axisMetadata.pointGroups)
			) {
				return true;
			}
			return false;
		}

		#createTagInput(input) {
			this.#awesompletes.push(
				new Awesomplete(input, {
					list: this.tags,
					autoFirst: true,
					data: function (item) {
						return {
							label: item.name,
							value: item.uuid,
						};
					},
					replace: function (suggestion) {
						this.input.value = suggestion.label;
					},
				}),
			);
		}

		#clearCache() {
			this.#cache = {};
		}

		#destroyAllTagInputs() {
			this.#awesompletes.forEach((awesomplete) => {
				awesomplete.destroy();
			});
			this.#awesompletes = [];
		}

		loadFromSavedSettings(axis) {
			if (wpd.utils.isEmptyObject(this.#cache)) {
				// check for saved settings only if cache is empty
				const metadata = axis.getMetadata();

				if (this.#hasPointGroups(metadata)) {
					const pointGroups = metadata.pointGroups;

					// load from saved settings
					this.#cache = {};

					if (pointGroups.x) {
						this.#cache.x = wpd.utils.deepClone(pointGroups.x);
					}

					if (pointGroups.y) {
						this.#cache.y = wpd.utils.deepClone(pointGroups.y);
					}
				}
			}
		}

		showSettingsPopup(axisDirection) {
			const axis = wpd.tree.getActiveAxes();

			if (axisDirection === "bar") {
				// for bar graphs, get orientation from axis
				const currentRotation = wpd.graphicsWidget.getRotation();
				const initialRotation = axis.getMetadata().initialRotation;

				let swapAxes = false;
				if (initialRotation !== undefined) {
					swapAxes = initialRotation % 180 !== 0;
				} else {
					swapAxes = currentRotation % 180 !== 0;
				}
				const originalAxisDirection = axis.getOrientation().axes.toLowerCase();
				const axisDirection = swapAxes
					? originalAxisDirection === "x"
						? "y"
						: "x"
					: originalAxisDirection;

				this.#currentAxisDirection = axisDirection;
			} else {
				// set current axis direction
				this.#currentAxisDirection = axisDirection;
			}

			// update the point group title bar with the axis direction
			this.$popup
				.querySelectorAll(".point-group-axis")
				.forEach(
					(span) => (span.innerHTML = this.#currentAxisDirection.toUpperCase()),
				);

			// check for cached point group settings
			if (
				!wpd.utils.isEmptyObject(this.#cache) &&
				this.#cache[this.#currentAxisDirection]?.length
			) {
				// load cached settings
				for (
					let i = 0;
					i < this.#cache[this.#currentAxisDirection].length;
					i++
				) {
					// add a row for each point group
					this.addSettingsRow(
						false,
						this.#cache[this.#currentAxisDirection][i].name,
					);
				}
			} else {
				// add two empty settings row
				this.addSettingsRow();
				this.addSettingsRow();
			}

			wpd.popup.show(PointGroups.#settingsPopupId);

			// nudge the popup down a bit
			this.$popup.style.top = "90px";
			this.$popup.style.zIndex = "1000";
			this.$layover.style.zIndex = "999";
		}

		closeSettingsPopup() {
			if (this.$popup.style.visibility === "visible") {
				wpd.popup.close(PointGroups.#settingsPopupId);

				const dataTypesPopupVisible =
					wpdDocument.getElementById("graph-axes-label-popup")?.style
						?.visibility === "visible";

				if (dataTypesPopupVisible) {
					this.$layover.style.visibility = "visible";
				}

				this.$layover.style.zIndex = "50";
			}

			this.$popup
				.querySelectorAll(".point-group-axis")
				.forEach((span) => (span.innerHTML = ""));

			// clean up all tag inputs
			this.#destroyAllTagInputs();

			// reset UI
			const $rows = this.$table.children;
			// spread the htmlCollection to an array to iterate
			[...$rows].forEach(($row) => {
				$row.remove();
			});

			// clear current axis direction
			this.#currentAxisDirection = undefined;
		}

		cancelSettingsPopup() {
			this.closeSettingsPopup();
		}

		showDeleteTuplePopup(yesCallback, noCallback) {
			const deleteText =
				"Deleting the central tendency point will delete other data points in this tuple.  Are you sure?";
			wpd.okCancelPopup.show(
				wpd.gettext("tuple-delete-title"),
				deleteText,
				yesCallback,
				noCallback,
				wpd.gettext("yes"),
				wpd.gettext("no"),
			);
		}

		showDeleteGroupPopup(message, yesCallback) {
			wpd.okCancelPopup.show(
				wpd.gettext("group-delete-title"),
				message,
				() => {
					this.$layover.style.visibility = "visible";
					this.$layover.style.zIndex = "999";
					yesCallback();
				},
				() => {
					this.$layover.style.visibility = "visible";
					this.$layover.style.zIndex = "999";
				},
				wpd.gettext("yes"),
				wpd.gettext("no"),
			);

			// nudge the popup down a bit
			this.$okCancelPopup.style.top = "120px";
			this.$okCancelPopup.style.zIndex = "1002";
			this.$layover.style.zIndex = "1001";
		}

		#generateSettingsRowHtml(wpd, index) {
			const groupText = "Point Group";
			const deleteGroupText = wpd.gettext("point-group-delete-group");

			// html templates
			const labelHTML = `<b>${this.#currentAxisDirection.toUpperCase()}-Axis</b> ${groupText} <span>${
				index + 1
			}</span>: `;
			const inputHTML = `
					<input
						id="point-group-${index}"
						index="${index}"
						type="text"
						class="point-group-input"
					/>
				`;
			const actionsHTML = `
					<input
						type="button"
						value="${deleteGroupText}"
						onclick="wpd.pointGroups.deleteSettingsRow(this);"
					/>
				`;

			return wpd.utils.oneLine(`
					<tr>
						<td>${labelHTML}</td>
						<td class="point-group-input-cell axis-data-type-input">
							${inputHTML}
							<br />
							<span class="error-message"></span>
						</td>
						<td class="point-group-actions-cell">${actionsHTML}</td>
					</tr>
				`);
		}

		#awesompleteSelectHandler(event) {
			const input = event.target
				.closest(".awesomplete")
				.querySelector(".point-group-input");

			wpd.alignAxes.validateDataType(input);
		}

		addSettingsRow(focus, value) {
			const maxRowIndex = this.$table.lastElementChild?.rowIndex ?? -1;
			const nextIndex = maxRowIndex + 1;
			const nextID = `point-group-${nextIndex}`;

			// create new input "row"
			const template = wpdDocument.createElement("template");
			template.innerHTML = this.#generateSettingsRowHtml(wpd, nextIndex);

			// append new row
			this.$table.appendChild(template.content.firstChild);

			const newInput = wpdDocument.getElementById(nextID);

			// initialize tag input
			this.#createTagInput(newInput);

			// attach listeners
			newInput.removeEventListener(
				"awesomplete-selectcomplete",
				this.#awesompleteSelectHandler,
			);
			newInput.addEventListener(
				"awesomplete-selectcomplete",
				this.#awesompleteSelectHandler,
			);
			newInput.removeEventListener("blur", this.#awesompleteSelectHandler);
			newInput.addEventListener("blur", this.#awesompleteSelectHandler);

			// set value if given
			if (value) {
				newInput.value = value;
			}

			// focus on new input
			if (focus) {
				newInput.focus();
			}

			// update first row delete button
			this.#updateFirstDeleteButton();
		}

		deleteSettingsRow(el) {
			this.showDeleteGroupPopup(
				"This will delete all data points in this point group. Are you sure?",
				this.#deleteSettingsRow.bind(this, el),
			);
		}

		#deleteSettingsRow(el) {
			const $targetRow = el.closest("tr");
			const $input = $targetRow.querySelector(".point-group-input");

			// collect deleted point group uuid
			this.#cacheDeletedPointGroup($input.value);

			// delete the target row
			$targetRow.remove();

			// re-index all rows
			const $rows = this.$table.children;
			// spread the htmlCollection to an array to iterate
			[...$rows].forEach(($row) => {
				const $cells = $row.children;
				const index = $row.rowIndex;

				$cells[0].querySelector("span").innerText = index + 1;
				$cells[1]
					.querySelector("input.point-group-input")
					.setAttribute("id", `point-group-${index}`);
			});

			// update first row delete button
			this.#updateFirstDeleteButton();
		}

		#cacheDeletedPointGroup(pointGroupName) {
			const tag = this.tags.find((tag) => tag.name === pointGroupName);

			if (tag) {
				if (dataEntryVM.pointGroupOverhaul) {
					if (!this.#cache.deletedPointGroupUuids) {
						this.#cache.deletedPointGroupUuids = {};
					}
					if (!this.#cache.deletedPointGroupUuids[this.#currentAxisDirection]) {
						this.#cache.deletedPointGroupUuids[this.#currentAxisDirection] = [];
					}
					this.#cache.deletedPointGroupUuids[this.#currentAxisDirection].push(
						tag.uuid,
					);
				} else {
					if (!this.#cache.deletedPointGroupUuids) {
						this.#cache.deletedPointGroupUuids = [];
					}
					this.#cache.deletedPointGroupUuids.push(tag.uuid);
				}
			}
		}

		#updateFirstDeleteButton() {
			const $firstRowDeleteButton = this.$table.firstChild.querySelector(
				".point-group-actions-cell input[type=button]",
			);

			if (this.$table.children.length > 1) {
				$firstRowDeleteButton.style.display = "";
			} else {
				$firstRowDeleteButton.style.display = "none";
			}
		}

		clearAllPointGroupInputs() {
			this.showDeleteGroupPopup(
				"This will delete all data points in these point groups. Are you sure?",
				this.#clearAllPointGroupInputs.bind(this),
			);
		}

		#clearAllPointGroupInputs() {
			const $inputs = this.$table.querySelectorAll(".point-group-input");

			$inputs.forEach(($input) => {
				// collect deleted point groups
				this.#cacheDeletedPointGroup($input.value);

				$input.value = "";
			});
		}

		cacheSettings() {
			// get dialog point groups
			const $inputs = this.$table.querySelectorAll(".point-group-input");

			const newPointGroups = [];
			const validationResults = [];
			$inputs.forEach(($input) => {
				if ($input.value) {
					// validate input
					validationResults.push(wpd.alignAxes.validateDataType($input));

					// collect new point group
					const tag = this.tags.find((tag) => tag.name === $input.value);
					newPointGroups.push({
						...wpd.utils.deepClone(tag),
						axis: this.#currentAxisDirection,
					});
				}
			});

			// if any input failed validation, do not set cache and immediately exit
			if (validationResults.some((result) => result !== true)) {
				return false;
			}

			if (newPointGroups.length) {
				this.#cache[this.#currentAxisDirection] = newPointGroups;
			} else {
				delete this.#cache[this.#currentAxisDirection];
			}

			// close popup
			this.closeSettingsPopup();
		}

		/**
		 * Update the point groups for the datasets associated with the given axis
		 * @param {wpd.Axis} axis
		 */
		updateAxisDatasetPointGroupsV2(axis, changedUuidMap) {
			const plotData = wpd.appData.getPlotData();
			const datasets = plotData.getDatasetsForAxis(axis);

			if (this.#hasPointGroups(axis.getMetadata())) {
				datasets.forEach((dataset) => {
					dataset.setPointGroups(this.getCenterTagUuids(axis));

					// update changed point groups in all data points
					// this does not update the tuple ids yet, that is done in
					// the updateAxisDatasetPointGroups functions for xy and bar
					// graph axes
					if (!_.isEmpty(changedUuidMap)) {
						for (const dataPoint of dataset.getAllPixels()) {
							const tupleDirection = dataPoint.metadata.pointGroupAxisDirection;
							// a point whose center could not be resolved has no tuple at all
							const tupleUuid =
								dataPoint.metadata.tuple?.[tupleDirection]?.tagUuid;

							if (tupleUuid && changedUuidMap[tupleDirection]) {
								const newUuid = changedUuidMap[tupleDirection][tupleUuid];
								if (newUuid) {
									dataPoint.metadata.tuple[tupleDirection].tagUuid = newUuid;
								}
							}
						}
					}
				});
			} else {
				datasets.forEach((dataset) => {
					// clear dataset point groups
					dataset.setPointGroups();
				});
			}
		}

		updateAxisDatasetPointGroups(axis, skipTupleRefresh = false) {
			const plotData = wpd.appData.getPlotData();
			const datasets = plotData.getDatasetsForAxis(axis);
			const metadata = axis.getMetadata();

			if (this.#hasPointGroups(metadata)) {
				// build point group uuid array for datasets
				const pointGroupUuids = [];

				const axisType = wpd.custom.identifyAxis(axis);
				const { yMainTag, barMainTag } = wpd.alignAxes.getGraphAxisTags(axis);
				let mainTagUuid;
				if (axisType === "xy") {
					// for backwards compatibility, add the main data type uuid
					// NOTE: defaulted to the y main data type, by itself this
					// uuid cannot determine what data points are of the main
					// data type
					mainTagUuid = yMainTag.uuid;

					pointGroupUuids.push(yMainTag.uuid);
				} else if (axisType === "bar") {
					mainTagUuid = barMainTag.uuid;

					pointGroupUuids.push(barMainTag.uuid);
				}

				metadata.pointGroups.x?.forEach((tag) =>
					pointGroupUuids.push(tag.uuid),
				);
				metadata.pointGroups.y?.forEach((tag) =>
					pointGroupUuids.push(tag.uuid),
				);

				datasets.forEach((dataset) => {
					// update datasets with point groups
					dataset.setPointGroups(pointGroupUuids);

					const dsMetadata = dataset.getMetadata();
					dataset.setMetadata(
						_.assign(dsMetadata, {
							pointGroups: [
								{
									tagUuid: mainTagUuid,
									axis: "center",
								},
								...(metadata.pointGroups.x?.map((tag) => {
									return { tagUuid: tag.uuid, axis: "x" };
								}) || []),
								...(metadata.pointGroups.y?.map((tag) => {
									return { tagUuid: tag.uuid, axis: "y" };
								}) || []),
							],
						}),
					);

					if (!skipTupleRefresh) {
						// update tuples
						this.refreshTuples(dataset);
					}
				});
			} else {
				datasets.forEach((dataset) => {
					// clear dataset point groups
					dataset.setPointGroups([]);

					if (!skipTupleRefresh) {
						// clear dataset tuples
						// there should only be one leave in each tuple
						dataset.removeTuple(0);
					}
				});
			}
		}

		getDatasetPointGroups(dataset, axisDirection) {
			if (dataEntryVM.pointGroupOverhaul) {
				const axis = wpd.appData.getPlotData().getAxesForDataset(dataset);
				const metadata = axis.getMetadata();

				if (
					this.#hasPointGroups(metadata) &&
					metadata.pointGroups[axisDirection]
				) {
					return metadata.pointGroups[axisDirection]?.map((tag) => tag.uuid);
				}
				return [];
			} else {
				const pointGroups = dataset.getPointGroups();
				return pointGroups ? pointGroups[axisDirection] || [] : [];
			}
		}

		/**
		 * Delete point group data points for the given axis
		 * @param {wpd.Axis} axis
		 */
		#deletePointGroupDataPointsV2(axis) {
			if (
				!wpd.utils.isEmptyObject(this.#cache) &&
				this.#cache.deletedPointGroupUuids
			) {
				for (const [axisDirection, tagUuids] of Object.entries(
					this.#cache.deletedPointGroupUuids,
				)) {
					const datasets = wpd.appData.getPlotData().getDatasetsForAxis(axis);

					datasets.forEach((dataset) => {
						const dataPointIndexesToDelete = dataset
							.getAllPixels()
							.reduce((result, dataPoint, index) => {
								if (
									dataPoint.metadata.pointGroupAxisDirection ===
										axisDirection &&
									tagUuids.includes(
										dataPoint.metadata.tuple[axisDirection].tagUuid,
									)
								) {
									result.push(index);
								}
								return result;
							}, []);

						dataPointIndexesToDelete
							.sort((a, b) => b - a)
							.forEach((index) => {
								dataset.removePixelAtIndex(index);

								wpd.events.dispatch("wpd.dataset.point.delete", {
									axes: axis,
									dataset: dataset,
									index: index,
								});
							});
					});
				}

				// remove deleled uuids from cache
				delete this.#cache.deletedPointGroupUuids;
			}
		}

		#deletePointGroupDataPoints(axis) {
			if (
				!wpd.utils.isEmptyObject(this.#cache) &&
				this.#cache.deletedPointGroupUuids &&
				this.#cache.deletedPointGroupUuids.length
			) {
				const datasets = wpd.appData.getPlotData().getDatasetsForAxis(axis);

				datasets.forEach((dataset) => {
					// get all indexes of data points with deleted point groups
					const pointGroupIndexes = dataset
						.getPointGroups()
						.reduce((result, uuid, index) => {
							if (this.#cache.deletedPointGroupUuids.includes(uuid)) {
								result.push(index);
							}
							return result;
						}, []);

					const dataPointIndexesToDelete = pointGroupIndexes.reduce(
						(result, pointGroupIndex) => {
							result.push(
								...dataset
									.getPixelIndexesInGroup(pointGroupIndex)
									.filter((index) => index !== null),
							);
							return result;
						},
						[],
					);

					dataPointIndexesToDelete
						.sort((a, b) => b - a)
						.forEach((index) => {
							dataset.removePixelAtIndex(index);

							wpd.events.dispatch("wpd.dataset.point.delete", {
								axes: axis,
								dataset: dataset,
								index: index,
							});
						});

					// remove deleted point groups from tuples
					pointGroupIndexes
						.sort((a, b) => b - a)
						.forEach((pointGroupIndex) =>
							dataset.removePointGroupFromTuples(pointGroupIndex),
						);
				});

				// remove deleled uuids from cache
				delete this.#cache.deletedPointGroupUuids;
			}
		}

		/**
		 * Regenerate center data point tuples
		 * @param {wpd.Axis} axis
		 */
		#refreshCenterDataPoints(axis) {
			const datasets = wpd.appData.getPlotData().getDatasetsForAxis(axis);

			datasets.forEach((dataset) => {
				dataset.getAllPixels().forEach((dataPoint) => {
					if (this.isCenterDataPoint(axis, dataPoint)) {
						dataPoint.metadata.tuple = this.generateTupleData(
							dataset,
							dataPoint,
							"center",
						);
					}
				});
			});
		}

		saveSettings(axis, skipTupleRefresh) {
			// delete deleted point group data points from datasets
			if (dataEntryVM.pointGroupOverhaul) {
				this.#deletePointGroupDataPointsV2(axis);
			} else {
				this.#deletePointGroupDataPoints(axis);
			}

			// mutate axis metadata
			const axisMetadata = axis.getMetadata();

			const changedUuidMap = {};
			if (dataEntryVM.pointGroupOverhaul) {
				// diff point group metadata
				const changes = diff(axisMetadata.pointGroups || {}, this.#cache);

				// create a map of maps for changed uuids in point groups
				changes.forEach((change) => {
					// collect uuid changes
					if (
						change.type === "CHANGE" &&
						change.path.length > 0 &&
						["x", "y"].includes(change.path[0]) &&
						change.path[change.path.length - 1] === "uuid"
					) {
						const axisDirection = change.path[0];
						if (!changedUuidMap[axisDirection]) {
							changedUuidMap[axisDirection] = {};
						}
						changedUuidMap[axisDirection][change.oldValue] = change.value;
					}
				});
			}

			axisMetadata.pointGroups = wpd.utils.deepClone(this.#cache);
			axis.setMetadata(axisMetadata);

			// update datasets associated with this axis
			if (dataEntryVM.pointGroupOverhaul) {
				this.updateAxisDatasetPointGroupsV2(axis, changedUuidMap);

				if (!_.isEmpty(changedUuidMap)) {
					// refresh center data points to remove any unnecessary tuple ids
					this.#refreshCenterDataPoints(axis);
				}
			} else {
				this.updateAxisDatasetPointGroups(axis, skipTupleRefresh);
			}

			this.resetSettings();
		}

		resetSettings() {
			this.#clearCache();
			this.closeSettingsPopup();
		}

		findAxisDirectionForTag(axis, { name, uuid }) {
			// no tag name or uuid provided
			if (!axis || (!name && !uuid)) {
				return undefined;
			}

			const metadata = axis.getMetadata();

			// no metadata present on axis
			if (!metadata) {
				return undefined;
			}

			const axisType = wpd.custom.identifyAxis(axis);

			if (axisType === "xy") {
				// no point groups configured
				if (!this.#hasPointGroups(metadata)) {
					return undefined;
				}

				// check each axis direction for the tag
				for (const [axisDirection, tags] of Object.entries(
					metadata.pointGroups,
				)) {
					if (tags.some((tag) => tag.name === name || tag.uuid === uuid)) {
						return axisDirection;
					}
				}
			} else if (axisType === "bar") {
				const orientationMap = {
					VERTICAL: "y",
					HORIZONTAL: "x",
				};

				return orientationMap[metadata.barHeaders.barOrientation];
			}

			return undefined;
		}

		getPointGroupFromDisplayName(axis, displayName) {
			// get axis metadata
			const metadata = axis.getMetadata();

			if (!this.#hasPointGroups(metadata)) {
				return null;
			}

			let tag;
			let axisDirection;

			if (dataEntryVM.pointGroupOverhaul) {
				// parse display name
				const [, direction, name] = /^\[([XY])\] (.+)$/.exec(displayName);

				// find the tag in the saved point groups settings
				axisDirection = direction.toLowerCase();
				tag = metadata.pointGroups[axisDirection]?.find(
					(tag) => tag.name === name,
				);
			} else {
				// parse display name
				const [, name] = /(?<=^\[[XY]\]) (.+)$/.exec(displayName);

				// find the tag in the saved point groups settings
				Object.entries(metadata.pointGroups).forEach(([direction, tags]) => {
					const foundTag = tags.find((tag) => tag.name === name);
					if (foundTag) {
						axisDirection = direction;
						tag = foundTag;
					}
				});
			}

			if (tag && axisDirection) {
				return {
					tag,
					axisDirection,
				};
			}
			return null;
		}

		/**
		 * Returns the axis-level point group tag for the given direction and tag
		 * name. Axis point groups are stored keyed by direction
		 * (`{ x: [...], y: [...] }`) and the tag objects do NOT carry an `axis`
		 * field, so callers must scope by the direction key rather than matching
		 * a non-existent `tag.axis` (DCTL-1772).
		 * @param {wpd.Axis} axis
		 * @param {string} axisDirection - "x" or "y"
		 * @param {string} tagName
		 * @returns {object|undefined} the matching point group tag, if any
		 */
		getAxisPointGroupTag(axis, axisDirection, tagName) {
			const metadata = axis?.getMetadata();
			return (metadata?.pointGroups?.[axisDirection] ?? []).find(
				(tag) => tag.name === tagName,
			);
		}

		/**
		 * Refresh tuples for a dataset
		 * @deprecated - DO NOT CALL
		 * should only be called from wpd.appData.getPlotData().deserialize
		 * keeping for backwards compatibility with point group settings saves
		 * before axis and dataset point group settings were introduced
		 */
		refreshTuples(dataset, tolerance = 1) {
			// get axis point group settings for given dataset
			const plotData = wpd.appData.getPlotData();
			const axis = plotData.getAxesForDataset(dataset);
			const metadata = axis.getMetadata();

			if (
				metadata &&
				metadata.axesLabelData &&
				this.#hasPointGroups(metadata)
			) {
				const axisType = wpd.custom.identifyAxis(axis);

				// get main data type tags
				let xMainTag, yMainTag, barMainTag;
				const mainTags = wpd.alignAxes.getGraphAxisTags(axis);
				if (axisType === "xy") {
					// default to searching for y-value data type tag
					// does not matter if searching for x or y
					({ xMainTag, yMainTag } = mainTags);
				} else if (axisType === "bar") {
					({ barMainTag } = mainTags);
				}

				// categorize the data points and their indexes by their
				// point group
				const mainDataPoints = [];
				const xPointGroupDataPoints = {};
				const yPointGroupDataPoints = {};
				dataset.getAllPixels().forEach((dataPoint, index) => {
					const dataPointTags = dataset.getTagsForDataPoint(dataPoint);

					if (
						(axisType === "xy" &&
							dataPointTags.some((tag) => tag.uuid === xMainTag.uuid) &&
							dataPointTags.some((tag) => tag.uuid === yMainTag.uuid)) ||
						(axisType === "bar" &&
							dataPointTags.some((tag) => tag.uuid === barMainTag.uuid))
					) {
						// collect main data type data points
						mainDataPoints.push({ dataPoint, index });
					} else {
						// collect x and y point group data points
						dataPointTags.some((tag) => {
							// collect x point group data points
							if (
								(!dataPoint.metadata.pointGroupAxisDirection ||
									dataPoint.metadata.pointGroupAxisDirection === "x") &&
								metadata.pointGroups?.x?.some((pointGroupTag) => {
									if (tag.uuid === pointGroupTag.uuid) {
										if (!xPointGroupDataPoints[tag.uuid]) {
											xPointGroupDataPoints[tag.uuid] = [];
										}

										xPointGroupDataPoints[tag.uuid].push({
											dataPoint,
											index,
										});

										return true;
									}
								})
							) {
								return true;
							}

							// collect y point group data points
							if (
								(!dataPoint.metadata.pointGroupAxisDirection ||
									dataPoint.metadata.pointGroupAxisDirection === "y") &&
								metadata.pointGroups?.y?.some((pointGroupTag) => {
									if (tag.uuid === pointGroupTag.uuid) {
										if (!yPointGroupDataPoints[tag.uuid]) {
											yPointGroupDataPoints[tag.uuid] = [];
										}

										yPointGroupDataPoints[tag.uuid].push({
											dataPoint,
											index,
										});

										return true;
									}
								})
							) {
								return true;
							}
						});
					}
				});

				// place all data points in tuples
				const tuples = [];

				// create a tuple for each main data point
				mainDataPoints.forEach(
					({ dataPoint: mainDataPoint, index: mainIndex }) => {
						const tuple = [mainIndex];

						// go through x point group data points
						metadata.pointGroups.x?.forEach((pointGroupTag) => {
							if (
								!xPointGroupDataPoints[pointGroupTag.uuid]?.some(
									({ dataPoint, index }) => {
										// check the opposite axis direction for same values
										if (Math.abs(mainDataPoint.y - dataPoint.y) <= tolerance) {
											tuple.push(index);
											return true;
										}
									},
								)
							) {
								// if no data points found, push null
								tuple.push(null);
							}
						});

						// go through y point group data points
						metadata.pointGroups.y?.forEach((pointGroupTag) => {
							if (
								!yPointGroupDataPoints[pointGroupTag.uuid]?.some(
									({ dataPoint, index }) => {
										// check the opposite axis direction for same values
										if (Math.abs(mainDataPoint.x - dataPoint.x) <= tolerance) {
											tuple.push(index);
											return true;
										}
									},
								)
							) {
								// if no data points found, push null
								tuple.push(null);
							}
						});

						// add the tuple to the collection
						tuples.push(tuple);
					},
				);

				// set the tuples on the dataset
				dataset.setAllTuples(tuples);

				// refresh metadata keys on the dataset
				wpd.custom.syncMetadataKeys(dataset);
			}
		}

		getDataPointPointGroupTagFromSettings(
			dataset,
			axisDirection,
			axisType,
			dataPointIndex,
		) {
			// find the tuple in the dataset
			const tupleIndex = dataset.getTupleIndex(dataPointIndex);

			if (tupleIndex > -1) {
				// get the point group index from the tuple and data point indexes
				const pointGroupIndex = dataset.getPointGroupIndexInTuple(
					tupleIndex,
					dataPointIndex,
				);

				if (pointGroupIndex > -1) {
					// find the point group tag
					// get axis point group settings for given dataset
					const plotData = wpd.appData.getPlotData();
					const axis = plotData.getAxesForDataset(dataset);
					const metadata = axis.getMetadata();

					if (
						axisDirection &&
						metadata &&
						metadata.axesLabelData &&
						this.#hasPointGroups(metadata)
					) {
						const xLength = metadata.pointGroups.x?.length ?? 0;
						const yLength = metadata.pointGroups.y?.length ?? 0;
						const xMaxIndex = xLength - 1;

						// ignore 0-indexed tuple (main data type)
						const adjustedIndex = pointGroupIndex - 1;

						if (pointGroupIndex === 0) {
							// main data type
							if (axisType === "xy") {
								return _.find(this.tags, {
									name: metadata.axesLabelData[axisDirection].dataType,
								});
							} else if (axisType === "bar") {
								return _.find(this.tags, {
									name: metadata.axesLabelData.bar.dataType,
								});
							}
						} else if (
							adjustedIndex >= 0 &&
							adjustedIndex <= xMaxIndex + yLength
						) {
							// make sure adjusted index is within bounds
							if (axisDirection === "x") {
								if (metadata.pointGroups.x) {
									return metadata.pointGroups.x[adjustedIndex];
								}
							} else {
								if (metadata.pointGroups.y) {
									return metadata.pointGroups.y[adjustedIndex - xLength];
								}
							}
						}
					}
				}
			}

			return null;
		}

		// updates saves with datasets that only have _groupNames and _tuples
		// to the current point group format
		updateToNewFormat(axis, dataset, legacyPointGroupUuids) {
			const axisType = wpd.custom.identifyAxis(axis);

			const generateError = (payload) =>
				new Error(
					`Failed to upgrade point groups to new format: ${JSON.stringify(
						payload,
					)}`,
					{
						cause: "Dataset main data type not one of axis label data types",
					},
				);

			// get dataset point group names
			const existingPointGroups = dataEntryVM.pointGroupOverhaul
				? legacyPointGroupUuids
				: dataset.getPointGroups();

			// the first point group should match one of the main data types
			// use this to determine the axis direction for the rest of the point groups
			// first point group from the dataset is (one of) the main data type(s)
			const existingMainDataTypeUuid = existingPointGroups.shift();

			let axisDirection;
			const mainTags = wpd.alignAxes.getGraphAxisTags(axis);
			const mainTagUuids = {};
			if (axisType === "xy") {
				const { xMainTag, yMainTag } = mainTags;

				// if the main data type is not one of the axis label data types, throw an error
				if (
					![xMainTag.uuid, yMainTag.uuid].includes(existingMainDataTypeUuid)
				) {
					throw generateError({
						xMainTagUuid: xMainTag.uuid,
						yMainTagUuid: yMainTag.uuid,
						existingMainDataTypeUuid,
					});
				}

				// NOTE: cannot use wpd.pointGroups.findAxisDirectionForTag for xy
				// as it relies on the new point group settings
				// assume the main data type axis is the point group axis
				axisDirection = yMainTag.uuid === existingMainDataTypeUuid ? "y" : "x";

				if (dataEntryVM.pointGroupOverhaul) {
					mainTagUuids.x = xMainTag.uuid;
					mainTagUuids.y = yMainTag.uuid;
				}
			} else if (axisType === "bar") {
				const { barMainTag } = mainTags;

				// if the main data type is not one of the axis label data types, throw an error
				if (barMainTag.uuid !== existingMainDataTypeUuid) {
					throw generateError({
						barMainTagUuid: barMainTag.uuid,
						existingMainDataTypeUuid,
					});
				}

				axisDirection = wpd.pointGroups.findAxisDirectionForTag(axis, {
					uuid: barMainTag.uuid,
				});

				if (dataEntryVM.pointGroupOverhaul) {
					mainTagUuids[axisDirection] = barMainTag.uuid;
				}
			}

			// add point groups to axis metadata
			this.#cache = {
				[axisDirection]: existingPointGroups.map((uuid) =>
					this.tags.find((tag) => tag.uuid === uuid),
				),
			};

			// save point groups to axis metadata
			this.saveSettings(axis, true);

			// refresh all data point tags based on settings
			this.#updateDataPointsWithAxisDataTypes(axis);

			if (dataEntryVM.pointGroupOverhaul) {
				// return the main data types and axis direction
				return mainTagUuids;
			} else {
				// refresh all tuples in datasets associated with this axis
				const datasets = wpd.appData.getPlotData().getDatasetsForAxis(axis);

				const errorDatasetUuids = [];

				datasets.forEach((d) => {
					// cache the existing tuples
					const existingTuples = JSON.parse(JSON.stringify(d.getAllTuples()));

					let tolerance = PointGroups.minTolerance;

					this.refreshTuples(d, tolerance);

					// check for changes in the tuples
					while (!_.isEqual(existingTuples, d.getAllTuples())) {
						// changes to tuples detected in one of the datasets
						// re-run with greater tolerance, unless we've reached max
						// tolerance
						if (tolerance <= PointGroups.maxTolerance) {
							this.refreshTuples(d, ++tolerance);
						} else {
							// restore existing tuples to prevent data corruption
							d._tuples = existingTuples;

							// store dataset uuid where refresh tuples failed
							const uuid = d.getMetadata().uuid;
							errorDatasetUuids.push(uuid);

							console.error(
								`Failed to upgrade point groups to new format: Could not refresh tuples for dataset ${uuid}`,
							);
						}
					}
				});

				if (errorDatasetUuids.length) {
					throw new Error(
						`Failed to upgrade point groups to new format: Could not refresh tuples for datasets ${errorDatasetUuids.join(
							", ",
						)}`,
					);
				}
			}
		}

		#updateDataPointsWithAxisDataTypes(axis) {
			const axesLabelData = axis.getMetadata().axesLabelData;

			if (axesLabelData) {
				const axisType = wpd.custom.identifyAxis(axis);

				if (axisType === "xy") {
					const dataTypes = {
						xAxisDataType: axesLabelData.x.dataType,
						xAxisUnit: axesLabelData.x.unit,
						xAxisDecimals: axesLabelData.x.decimals,
						xAxisChangeFromBaseline: axesLabelData.x.changeFromBaseline,
						yAxisDataType: axesLabelData.y.dataType,
						yAxisUnit: axesLabelData.y.unit,
						yAxisDecimals: axesLabelData.y.decimals,
						yAxisChangeFromBaseline: axesLabelData.y.changeFromBaseline,
					};

					wpd.xyGraphEdit.updateGraphPointsWithAxisDataTypes(axis, dataTypes);
				} else if (axisType === "bar") {
					// bar axis label data is nested under `.bar` (mirroring the
					// `.x`/`.y` nesting above); reading the un-nested keys yields
					// undefined, which blanks the Unit value, nulls the value tag,
					// and rounds the value with default decimals on conversion
					// (DCTL-1771).
					const barLabelData = axesLabelData.bar ?? {};
					const dataTypes = {
						barAxisDataType: barLabelData.dataType,
						barAxisUnit: barLabelData.unit,
						barAxisDecimals: barLabelData.decimals,
						barAxisChangeFromBaseline: barLabelData.changeFromBaseline,
					};

					wpd.barGraphEdit.updateGraphPointsWithAxisDataTypes(axis, dataTypes);
				}
			}
		}

		isSupportedAxis(axis) {
			return PointGroups.supportedAxisTypes.includes(
				wpd.custom.identifyAxis(axis),
			);
		}

		syncTupleDataPointValue(
			dataset,
			axis,
			dataPointIndex,
			sharedValueAxisDirection,
			sharedTag,
			sharedValue,
		) {
			// only xy graphs are supported
			// bar graph shared values should be updated via the bar headers
			const axisType = wpd.custom.identifyAxis(axis);
			if (axisType !== "xy") {
				return;
			}

			const metadata = axis.getMetadata();

			// get point group direction from given axis direction
			// if the data point value is for the x-axis, the point group
			// direction is the y-axis
			const axisDirection = sharedValueAxisDirection === "x" ? "y" : "x";

			// check that the axis direction has point groups defined
			if (
				this.#hasPointGroups(metadata) &&
				metadata.pointGroups[axisDirection]
			) {
				// get the point group data point tag
				// e.g. commonly the axis direction is y and the main data point tag is mean
				let mainAxisTag;

				if (dataEntryVM.pointGroupOverhaul) {
					mainAxisTag = this.getDataPointPointGroupTag(
						dataset,
						dataPointIndex,
						axisDirection,
					);
				} else {
					mainAxisTag = this.getDataPointPointGroupTagFromSettings(
						dataset,
						axisDirection,
						axisType,
						dataPointIndex,
					);
				}

				// get data point tag for the value in the point group direction
				const dataPointTag = Object.values(
					dataset.getPixel(dataPointIndex).metadata || {},
				).find(
					(data) => data.axis === axisDirection && data.axisType === "value",
				)?.tag;

				// sync tuple to new shared value
				if (mainAxisTag.uuid === dataPointTag.uuid) {
					let count = 0;

					if (dataEntryVM.pointGroupOverhaul) {
						const tupleDataPointIndexes = this.getTupleDataPointIndexes(
							dataset,
							dataset.getPixel(dataPointIndex),
							axisDirection,
						);

						for (const index of tupleDataPointIndexes) {
							// get the tuple data point
							const dataPoint = dataset.getPixel(index);

							// find the key where:
							//   - it has the same tag as the given data point
							//   - it has the same axis direction as the given data point
							//   - it is a value
							const sharedValueKey = Object.entries(dataPoint.metadata).find(
								([, data]) =>
									data &&
									data.axisType === "value" &&
									data.axis === sharedValueAxisDirection &&
									data.tag?.uuid === sharedTag.uuid,
							)?.[0];

							// skip if shared value key cannot be found
							// or if value is already the shared value
							if (
								sharedValueKey &&
								dataPoint.metadata[sharedValueKey].value !== sharedValue
							) {
								// finally set the value
								dataPoint.metadata[sharedValueKey].value = sharedValue;

								count++;
							}
						}
					} else {
						// get the tuple
						const tupleIndex = dataset.getTupleIndex(dataPointIndex);
						const tuple = dataset.getTuple(tupleIndex);

						// get the point group index in the tuple
						const pointGroupIndex = dataset.getPointGroupIndexInTuple(
							tupleIndex,
							dataPointIndex,
						);

						// update tuple data point values along point group direction to main data point value
						// skip the main data point index (first index)
						for (let i = 0; i < tuple.length; i++) {
							// skip point group index of given data point index
							if (i === pointGroupIndex) {
								continue;
							}

							const index = tuple[i];

							// skip nulls
							if (index === null) {
								continue;
							}

							// get the tuple data point
							const dataPoint = dataset.getPixel(index);

							// find the key where:
							//   - it has the same tag as the given data point
							//   - it has the same axis direction aas the given data point
							//   - it is a value
							const sharedValueKey = Object.entries(dataPoint.metadata).find(
								([, data]) =>
									data &&
									data.axisType === "value" &&
									data.axis === sharedValueAxisDirection &&
									data.tag?.uuid === sharedTag.uuid,
							)?.[0];

							// skip if shared value key cannot be found
							// or if value is already the shared value
							if (
								sharedValueKey &&
								dataPoint.metadata[sharedValueKey].value !== sharedValue
							) {
								// finally set the value
								dataPoint.metadata[sharedValueKey].value = sharedValue;

								count++;
							}
						}
					}

					if (count > 0) {
						dataEntryVM.showNotification({
							message: `
									Synced ${sharedValueAxisDirection}-value to "${sharedValue}" for ${count} data points in the tuple
								`,
							status: "info",
						});
					}
				}
			}
		}

		// Determines a map of point group groupIndex to color for the given axis:
		// {
		// 	x: {
		// 		0: "rgba(0, 255, 0, 0.9)",
		// 		1: 'rgba(255, 68, 0, 0.9)',
		// 		2: 'rgba(0, 72, 253, 0.9)',
		//
		// 	},
		// 	y: {
		// 		0: "rgba(0, 255, 0, 0.9)",
		// 		1: 'rgba(255, 68, 0, 0.9)',
		// 		2: 'rgba(255, 140, 0, 0.9)',
		// 		3: 'rgba(0, 72, 253, 0.9)',
		// 		4: 'rgba(0, 255, 217, 0.9)',
		// 	},
		// }
		calculatePointGroupColors(axis, alpha) {
			if (!dataEntryVM.pointGroupColors) {
				return { x: new Map(), y: new Map() };
			}

			// Helper functions
			const getSortPriority = (str) => {
				const sortKeywordList = [
					...PointGroups.colorSortKeywords.negative,
					...PointGroups.colorSortKeywords.positive,
				];
				const lowerStr = str.toLowerCase();
				const foundIndex = _.findIndex(sortKeywordList, (keyword) =>
					lowerStr.includes(keyword),
				);
				return foundIndex !== -1 ? foundIndex : 999;
			};

			const sortByStatisticalPriority = (a, b) => {
				const priorityA = getSortPriority(a);
				const priorityB = getSortPriority(b);
				if (priorityA === priorityB) {
					return a.localeCompare(b);
				}
				return priorityA - priorityB;
			};

			const isKeywordMatch = (name, keywords) => {
				return _.findIndex(keywords, (keyword) => name.includes(keyword)) >= 0;
			};

			const processPointGroups = (groups, indexOffset = 0) => {
				if (!groups) return new Map();

				const groupsWithIndices = groups.map((group, index) => ({
					group,
					originalIndex: index,
				}));

				const sortedGroupsWithIndices = [...groupsWithIndices].sort((a, b) =>
					sortByStatisticalPriority(a.group.name, b.group.name),
				);

				const colorMap = new Map();
				const seenNames = new Set();
				let negativeIndex = 0;
				let positiveIndex = 0;

				// The central point is green
				colorMap.set(0, wpd.Colors.good);

				// Process negative keywords first
				sortedGroupsWithIndices.forEach((item) => {
					const name = item.group.name.toLowerCase();
					if (isKeywordMatch(name, PointGroups.colorSortKeywords.negative)) {
						const color = alpha
							? PointGroups.colorsA.negative[negativeIndex]
							: PointGroups.colors.negative[negativeIndex];
						colorMap.set(item.originalIndex + indexOffset + 1, color);

						if (!seenNames.has(name)) {
							seenNames.add(name);
							if (negativeIndex < PointGroups.colors.negative.length - 1) {
								negativeIndex++;
							}
						}
					}
				});

				// Process positive keywords in reverse order
				[...sortedGroupsWithIndices].reverse().forEach((item) => {
					const name = item.group.name.toLowerCase();
					if (isKeywordMatch(name, PointGroups.colorSortKeywords.positive)) {
						const color = alpha
							? PointGroups.colorsA.positive[positiveIndex]
							: PointGroups.colors.positive[positiveIndex];
						colorMap.set(item.originalIndex + indexOffset + 1, color);

						if (!seenNames.has(name)) {
							seenNames.add(name);
							if (positiveIndex < PointGroups.colors.positive.length - 1) {
								positiveIndex++;
							}
						}
					}
				});

				return colorMap;
			};

			// Main processing
			const pointGroups = axis.getMetadata()?.pointGroups;
			if (!pointGroups) {
				return { x: new Map(), y: new Map() };
			}

			const xMaxIndex = pointGroups.x?.length ?? 0;
			const xPointGroupIndexColorMap = processPointGroups(pointGroups.x, 0);
			const yPointGroupIndexColorMap = processPointGroups(
				pointGroups.y,
				xMaxIndex,
			);

			return {
				x: xPointGroupIndexColorMap,
				y: yPointGroupIndexColorMap,
			};
		}

		/**
		 * Returns the tuple id of the given data point
		 * @param {wpd.DataPoint} dataPoint - Data point to get tuple index for
		 * @returns {Number} - Tuple id of the given data point
		 */
		#getDataPointTupleId(dataPoint, axisDirection) {
			return dataPoint.metadata.tuple?.[axisDirection]?.id;
		}

		/**
		 * Returns the indexes of the data points in the tuple that are related to the given data point
		 * @param {wpd.Dataset} dataset - Dataset to get related data point indexes for
		 * @param {wpd.DataPoint} dataPoint - Data point to get related data point indexes for
		 * @param {String} axisDirection - Optional axis direction to get related data point indexes for
		 * @returns {Array} - Indexes of the data points in the tuple that are related to the given data point
		 */
		getTupleDataPointIndexes(dataset, dataPoint, axisDirection) {
			// get point group settings
			const axis = wpd.appData.getPlotData().getAxesForDataset(dataset);
			const pointGroupSettings = axis.getMetadata().pointGroups;

			const tupleIds = [];

			// if axis direction is provided, only get related data point indexes for that direction
			// otherwise, get related data point indexes for all point group directions
			const directions = axisDirection
				? [axisDirection]
				: Object.keys(pointGroupSettings);

			for (const direction of directions) {
				const tupleId = this.#getDataPointTupleId(dataPoint, direction);
				if (tupleId) {
					tupleIds.push(tupleId);
				}
			}

			return this.sortDataPointIndexesByCenterFirst(
				axis,
				dataset,
				dataset.getAllPixels().reduce((result, dataPoint, index) => {
					if (
						Object.values(dataPoint.metadata.tuple || []).some((tuple) =>
							tupleIds.includes(tuple.id),
						)
					) {
						result.push(index);
					}

					return result;
				}, []),
			);
		}

		/**
		 * Generates a tuple id for the given data point
		 * @param {wpd.Dataset} dataset - Dataset to generate tuple id for
		 * @param {wpd.DataPoint} dataPoint - Data point to generate tuple id for
		 * @param {String} axisDirection - Axis direction to generate tuple id for
		 * @returns {String} - Tuple id for the given data point
		 */
		/**
		 * Returns the pixel proximity tolerance for matching a satellite data
		 * point to its center on the shared axis. Derived from the axis
		 * calibration so it scales with the graph's pixel dimensions rather than
		 * a single hardcoded constant (too tight on large charts, too loose on
		 * small ones). Floored at PointGroups.maxTolerance so it is never tighter
		 * than the historical behavior - it can only loosen on larger graphs,
		 * never regress a match that works today. (DCTL-1768)
		 * @param {wpd.Axis} axis
		 * @param {String} sharedPixelDirection - "x" or "y", in the image frame
		 * @returns {Number} tolerance in image pixels
		 */
		#getProximityTolerance(axis, sharedPixelDirection) {
			const calibration = axis?.calibration;
			if (!calibration || typeof calibration.getCount !== "function") {
				return PointGroups.maxTolerance;
			}

			const count = calibration.getCount();
			const coord = sharedPixelDirection === "x" ? "px" : "py";
			const values = [];
			for (let i = 0; i < count; i++) {
				const point = calibration.getPoint(i);
				if (point && typeof point[coord] === "number") {
					values.push(point[coord]);
				}
			}

			if (values.length < 2) {
				return PointGroups.maxTolerance;
			}

			const span = Math.max(...values) - Math.min(...values);
			return Math.max(
				span * PointGroups.proximityToleranceFraction,
				PointGroups.maxTolerance,
			);
		}

		/**
		 * Returns the image-frame pixel coordinate that the shared axis runs
		 * along. Swaps when the axis was calibrated on a rotated page, falling
		 * back to the current rotation for axes saved without initialRotation
		 * (same derivation as showSettingsPopup).
		 * @param {wpd.Axis} axis
		 * @param {String} sharedValueDirection - "x" or "y", in the datum frame
		 * @returns {String} "x" or "y"
		 */
		#getSharedPixelDirection(axis, sharedValueDirection) {
			const initialRotation = axis.getMetadata().initialRotation;
			const swapAxes =
				initialRotation !== undefined
					? initialRotation % 180 !== 0
					: wpd.graphicsWidget.getRotation() % 180 !== 0;

			if (!swapAxes) {
				return sharedValueDirection;
			}
			return sharedValueDirection === "x" ? "y" : "x";
		}

		#generateTupleId(dataset, dataPoint, axisDirection) {
			const axis = wpd.appData.getPlotData().getAxesForDataset(dataset);

			// The shared (category) axis is always the complement of the value axis
			// the point group sits on. `axisDirection` and the stored `axis` fields on
			// each datum are recorded in the same frame, so the complement holds at
			// any page rotation and no correction applies here.
			//
			// The swapAxes branch used to return `axisDirection` itself, collapsing
			// both onto one axis, and a swapXY flip then ran on top of that. The pair
			// cancelled only when initialRotation was 0; on an axis calibrated while
			// the page was rotated both fired, so the category half of the tuple id
			// read the value axis. Row identity degenerated to the bar's own value -
			// two bars sharing a value merged into one row, and placing a satellite
			// was rejected as a duplicate tuple.
			const sharedValueDirection = axisDirection === "x" ? "y" : "x";

			// Pixel positions are stored in the unrotated image frame, so on an axis
			// calibrated while the page was rotated the shared axis runs along the
			// other pixel coordinate. The tag checks stay on sharedValueDirection;
			// only the pixel distance comparisons use this.
			const sharedPixelDirection = this.#getSharedPixelDirection(
				axis,
				sharedValueDirection,
			);

			// find the center data point
			const axisType = wpd.custom.identifyAxis(axis);
			const centerTags = this.getCenterTagUuids(axis);
			// proximity tolerance scaled to this graph's calibration (DCTL-1768)
			const proximityTolerance = this.#getProximityTolerance(
				axis,
				sharedPixelDirection,
			);
			let centerDataPoint;
			if (this.isCenterDataPoint(axis, dataPoint)) {
				centerDataPoint = dataPoint;
			} else {
				// get the center data point that matches the shared value datum of the given data point
				if (axisType === "xy") {
					const xyCenterFilter = (dp) =>
						this.isCenterDataPoint(axis, dp) &&
						Object.values(dp.metadata).some(
							(datum) =>
								datum.axis === sharedValueDirection &&
								datum.axisType === "value" &&
								datum.tag?.uuid === centerTags[sharedValueDirection] &&
								Math.abs(
									dp[sharedPixelDirection] - dataPoint[sharedPixelDirection],
								) <= proximityTolerance,
						);

					if (dataEntryVM.pointGroupProximityFixes) {
						// find the closest center point on the shared axis
						centerDataPoint = dataset.getAllPixels().reduce((closest, dp) => {
							if (!xyCenterFilter(dp)) return closest;
							const distance = Math.abs(
								dp[sharedPixelDirection] - dataPoint[sharedPixelDirection],
							);
							if (
								!closest ||
								distance <
									Math.abs(
										closest[sharedPixelDirection] -
											dataPoint[sharedPixelDirection],
									)
							) {
								return dp;
							}
							return closest;
						}, null);
					} else {
						centerDataPoint = dataset.getAllPixels().find(xyCenterFilter);
					}
				} else if (axisType === "bar") {
					// Same quantity as sharedValueDirection above - the category axis -
					// so read it from there rather than re-deriving it. The old
					// derivation came from headerOrientation with its own rotation
					// correction, which let the two disagree: on an axis calibrated
					// while the page was rotated they resolved to opposite axes, so
					// this filter picked up the value tags and every bar in the dataset
					// matched every other one. It was also wrong for a legacy axis with
					// no initialRotation viewed under rotation, where swapXY cannot
					// compensate (the difference is NaN).
					const categoryData = Object.values(dataPoint.metadata).filter(
						(datum) => datum.axis === sharedValueDirection,
					);

					const barCenterFilter = (dp) =>
						this.isCenterDataPoint(axis, dp) &&
						categoryData.every((categoryDatum) =>
							Object.values(dp.metadata).some(
								(datum) => datum.tag?.uuid === categoryDatum.tag?.uuid,
							),
						) &&
						Math.abs(
							dp[sharedPixelDirection] - dataPoint[sharedPixelDirection],
						) <= proximityTolerance;

					if (dataEntryVM.pointGroupProximityFixes) {
						centerDataPoint = dataset.getAllPixels().reduce((closest, dp) => {
							if (!barCenterFilter(dp)) return closest;
							const distance = Math.abs(
								dp[sharedPixelDirection] - dataPoint[sharedPixelDirection],
							);
							if (
								!closest ||
								distance <
									Math.abs(
										closest[sharedPixelDirection] -
											dataPoint[sharedPixelDirection],
									)
							) {
								return dp;
							}
							return closest;
						}, null);
					} else {
						centerDataPoint = dataset.getAllPixels().find(barCenterFilter);
					}
				} else {
					console.error(`invalid axis type: ${axisType}`);
				}
			}

			if (!centerDataPoint) {
				console.error(
					`cannot find center data point on ${
						dataset.name
					} for: ${JSON.stringify(dataPoint.metadata, null, 2)}`,
				);

				return;
			}

			// get the shared value datum in the center data point
			let sharedValueId;
			if (axisType === "xy") {
				const sharedCenterDatum = Object.values(centerDataPoint.metadata).find(
					(datum) =>
						datum.axis === sharedValueDirection &&
						datum.axisType === "value" &&
						datum.tag?.uuid === centerTags[sharedValueDirection],
				);

				sharedValueId = `${sharedCenterDatum.tag.name}=${sharedCenterDatum.value}`;
			} else if (axisType === "bar") {
				const sharedCenterData = Object.values(centerDataPoint.metadata)
					.filter((datum) => datum.axis === sharedValueDirection)
					.sort((a, b) => {
						const tagCompare = a.tag.name.localeCompare(b.tag.name);
						if (tagCompare !== 0) {
							return tagCompare;
						}
						// if tag names are equal, compare by value
						if (a.value < b.value) return -1;
						if (a.value > b.value) return 1;
						return 0;
					});

				if (sharedCenterData.length > 0) {
					sharedValueId = sharedCenterData
						.map(
							(sharedCenterDatum) =>
								`${sharedCenterDatum.tag.name}=${sharedCenterDatum.value}`,
						)
						.join("&");
				} else {
					// Forest-plot / position-encoded category: rows are distinguished
					// by y-pixel-position + branch composite key, with no tag on the
					// category (shared-value) axis, so the tag-based id above would be
					// empty. Synthesize a row-stable shared id from the center data
					// point so the tuple id is non-empty and still groups the central
					// tendency with its satellites. The center data point is the same
					// resolved pixel for every member of a row, so this id is identical
					// across the row yet differs between rows. (DCTL-1765)
					const rowKey =
						centerDataPoint.metadata.compositeKey ??
						centerDataPoint[sharedValueDirection];
					sharedValueId = rowKey != null ? `row=${rowKey}` : "";
				}
			}

			// get the center value datum in the center data point
			const centerDatum = Object.values(centerDataPoint.metadata).find(
				(datum) =>
					datum.axis === axisDirection &&
					datum.axisType === "value" &&
					datum.tag?.uuid === centerTags[axisDirection],
			);
			const centerValueId = `${centerDatum.tag.name}=${centerDatum.value}`;

			const ids = [];
			if (sharedValueId && centerValueId) {
				ids.push(`${sharedValueDirection}:${sharedValueId}`.toLowerCase());
				ids.push(`${axisDirection}:${centerValueId}`.toLowerCase());
			} else {
				console.error(
					`cannot find both center tag uuids for ${axisDirection} axis on ${dataset.name}`,
				);
			}

			return ids.join("||");
		}

		/**
		 * Generates a tuple data for the given data point
		 * @param {wpd.Dataset} dataset - Dataset to generate tuple data for
		 * @param {wpd.DataPoint} dataPoint - Data point to generate tuple data for
		 * @param {String} axisDirection - Axis direction to generate tuple data for
		 * @param {String} tagUuid - Tag uuid to generate tuple for
		 * @returns {Object|undefined} - Tuple data for the given data point, or
		 *   undefined when no tuple id could be minted
		 */
		#generateTupleDatum(dataset, dataPoint, axisDirection, tagUuid) {
			// A datum without an id is rejected by the server on save ("Point group
			// tuple is missing tupleId"), and once written onto a point it is
			// re-injected on every load, so the record can never be saved again.
			// No center point (or no shared value on it) means no id, so no datum.
			// (DCTL-2561)
			const id = this.#generateTupleId(dataset, dataPoint, axisDirection);
			if (!id) {
				return;
			}

			return {
				[axisDirection]: {
					id,
					...(tagUuid
						? { tagUuid }
						: {
								tagUuid: dataset.getMetadata().pointGroups.find(
									(pointGroup) =>
										pointGroup.axis === axisDirection &&
										!pointGroup.center &&
										Object.values(dataPoint.metadata)
											.filter(
												(metadatum) =>
													metadatum.tag &&
													metadatum.axis === axisDirection &&
													metadatum.axisType === "value",
											)
											.map((metadatum) => metadatum.tag.uuid)
											.includes(pointGroup.tagUuid),
								)?.tagUuid,
						  }),
				},
			};
		}

		/**
		 * Generates tuple data for the given data point
		 * @param {wpd.Dataset} dataset - Dataset to generate tuple data for
		 * @param {wpd.DataPoint} dataPoint - Data point to generate tuple data for
		 * @param {String} axisDirection - Axis direction to generate tuple data for
		 * @param {String} tagUuid - Optional tag uuid to set on the tuple
		 * @returns {Object|undefined} - Tuple data for the given data point, or
		 *   undefined when no side could mint a tuple id
		 */
		generateTupleData(dataset, dataPoint, axisDirection, tagUuid) {
			// if axis direction is provided and the dataset doesn't have point groups for that direction, return
			if (
				axisDirection &&
				axisDirection !== "center" &&
				!dataset.hasPointGroups(axisDirection)
			) {
				return;
			}

			// if this is a central tendie, return the tuple data for all point group directions
			if (axisDirection === "center") {
				const pointGroupSettings = dataset.getMetadata()?.pointGroups;

				if (!pointGroupSettings) {
					console.error(
						"point group settings not found while generating tuple",
					);

					return;
				}

				const xDatum = dataset.hasPointGroups("x")
					? this.#generateTupleDatum(
							dataset,
							dataPoint,
							"x",
							pointGroupSettings.find(
								(pointGroup) => pointGroup.axis === "x" && pointGroup.center,
							)?.tagUuid,
						)
					: undefined;
				const yDatum = dataset.hasPointGroups("y")
					? this.#generateTupleDatum(
							dataset,
							dataPoint,
							"y",
							pointGroupSettings.find(
								(pointGroup) => pointGroup.axis === "y" && pointGroup.center,
							)?.tagUuid,
						)
					: undefined;

				// Spreading two undefined datums would yield {}, which is still truthy
				// to callers that test `if (tupleData)`, so return undefined explicitly.
				if (!xDatum && !yDatum) {
					return;
				}

				return { ...xDatum, ...yDatum };
			}

			// otherwise, return the tuple data for the given axis direction
			return this.#generateTupleDatum(
				dataset,
				dataPoint,
				axisDirection,
				tagUuid,
			);
		}

		/**
		 * Returns the point group tag uuid for the given data point
		 * @param {wpd.Dataset} dataset - Dataset to get point group tag uuid for
		 * @param {Number} dataPointIndex - Data point index to get point group tag uuid for
		 * @param {String} axisDirection - Axis direction to get point group tag uuid for
		 * @returns {String} - Point group tag uuid for the given data point
		 */
		getDataPointPointGroupTag(dataset, dataPointIndex, axisDirection) {
			const dataPoint = dataset.getPixel(dataPointIndex);
			const axis = wpd.appData.getPlotData().getAxesForDataset(dataset);
			const tagUuid = this.getCenterTagUuids(axis)[axisDirection];

			if (
				this.isCenterDataPoint(axis, dataPoint) ||
				dataPoint.metadata.pointGroupAxisDirection === "center" ||
				!dataPoint.metadata.tuple?.[axisDirection]
			) {
				// for center data points or tuple data points of the shared direction
				// return the tag from the axes label data. The optional chain also
				// covers legacy data points where tuple is undefined entirely
				// (DCTL-1763: surfaced by mid-conversion calls from
				// updateToNewFormat before tuples have been filled in).
				return wpd.tagGroups.tags.find((tag) => tag.uuid === tagUuid);
			} else {
				// otherwise, return the tag from the tuple data
				return wpd.tagGroups.tags.find(
					(tag) => tag.uuid === dataPoint.metadata.tuple[axisDirection].tagUuid,
				);
			}
		}

		/**
		 * Returns the center tag uuids for the given axis
		 * @param {wpd.Axis} axis - Axis to get center tag uuids for
		 * @returns {Object} - Center tag uuids for the given axis
		 */
		getCenterTagUuids(axis) {
			if (!axis) {
				return;
			}

			const axisType = wpd.custom.identifyAxis(axis);
			const { xMainTag, yMainTag, barMainTag } =
				wpd.alignAxes.getGraphAxisTags(axis);

			if (axisType === "xy") {
				return {
					x: xMainTag.uuid,
					y: yMainTag.uuid,
				};
			} else if (axisType === "bar") {
				// for bar graphs, translate to the axis direction
				const axisDirection = Object.keys(axis.getMetadata().pointGroups)[0];
				return {
					[axisDirection]: barMainTag.uuid,
				};
			}

			return;
		}

		/**
		 * Returns true if the given data point is a center data point
		 * @param {wpd.Axis} axis - Axis to check if the data point is a center data point for
		 * @param {wpd.DataPoint} dataPoint - Data point to check if it is a center data point
		 * @returns {Boolean} - True if the given data point is a center data point
		 */
		isCenterDataPoint(axis, dataPoint) {
			if (!dataPoint.metadata) {
				return false;
			}

			const centerTags = this.getCenterTagUuids(axis);

			const isCenterData = [];
			for (const axisDirection of Object.keys(centerTags)) {
				isCenterData.push(
					Object.values(dataPoint.metadata).some(
						(metadatum) =>
							metadatum.axisType === "value" &&
							metadatum.axis === axisDirection &&
							metadatum.tag?.uuid === centerTags[axisDirection],
					),
				);
			}

			return isCenterData.every((isCenterDatum) => isCenterDatum);
		}

		/**
		 * Returns true if the given center data point has tuple data points in any direction
		 * @param {wpd.Dataset} dataset - Dataset to check if the center data point has tuple data points for
		 * @param {wpd.DataPoint} centerDataPoint - Center data point to check if it has tuple data points for
		 * @returns {Boolean} - True if the given center data point has tuple data points in any direction
		 */
		hasTupleDataPoints(dataset, centerDataPoint) {
			const axis = wpd.appData.getPlotData().getAxesForDataset(dataset);

			if (centerDataPoint.metadata.tuple) {
				const directions = Object.keys(centerDataPoint.metadata.tuple);

				for (const direction of directions) {
					if (centerDataPoint.metadata.tuple[direction]) {
						const tupleId = this.#getDataPointTupleId(
							centerDataPoint,
							direction,
						);

						return dataset
							.getAllPixels()
							.some(
								(dataPoint) =>
									!this.isCenterDataPoint(axis, dataPoint) &&
									dataPoint.metadata.tuple &&
									this.#getDataPointTupleId(dataPoint, direction) === tupleId,
							);
					}
				}
			}

			return false;
		}

		/**
		 * Returns true if the given center data point has tuple data points in the other direction
		 * @param {wpd.Dataset} dataset - Dataset to check if the center data point has other tuple data points for
		 * @param {wpd.DataPoint} centerDataPoint - Center data point to check if it has other tuple data points for
		 * @param {String} direction - Direction to check if the center data point has other tuple data points for
		 * @returns {Boolean} - True if the given center data point has other tuple data points
		 */
		hasOtherTupleDataPoints(dataset, centerDataPoint, direction) {
			const axis = wpd.appData.getPlotData().getAxesForDataset(dataset);
			const otherDirection = direction === "x" ? "y" : "x";
			if (
				centerDataPoint.metadata.tuple &&
				centerDataPoint.metadata.tuple[otherDirection]
			) {
				const tupleId = this.#getDataPointTupleId(
					centerDataPoint,
					otherDirection,
				);

				return dataset
					.getAllPixels()
					.some(
						(dataPoint) =>
							!this.isCenterDataPoint(axis, dataPoint) &&
							dataPoint.metadata.tuple &&
							this.#getDataPointTupleId(dataPoint, otherDirection) === tupleId,
					);
			}
			return false;
		}

		/**
		 * Returns the given point indexes sorted by center first
		 * @param {wpd.Axis} axis - Axis to sort point indexes for
		 * @param {wpd.Dataset} dataset - Dataset to sort point indexes for
		 * @param {Number[]} pointIndexes - Point indexes to sort
		 * @returns {Number[]} - Given point indexes sorted by center first
		 */
		sortDataPointIndexesByCenterFirst(axis, dataset, pointIndexes) {
			// if no point indexes are provided, use all point indexes
			const indexes =
				pointIndexes ??
				Array.from({ length: dataset.getAllPixels().length }, (_, i) => i);
			return indexes.toSorted((a, b) => {
				const aIsCenter = this.isCenterDataPoint(axis, dataset.getPixel(a));
				const bIsCenter = this.isCenterDataPoint(axis, dataset.getPixel(b));
				return aIsCenter ? -1 : bIsCenter ? 1 : 0;
			});
		}

		/**
		 * Returns the given data points sorted by center first
		 * @param {wpd.Axis} axis - Axis to sort data points for
		 * @param {wpd.DataPoint[]} dataPoints - Data points to sort
		 * @returns {wpd.DataPoint[]} - Given data points sorted by center first
		 */
		sortDataPointsByCenterFirst(axis, dataPoints) {
			return dataPoints.toSorted((a, b) => {
				const aIsCenter = this.isCenterDataPoint(axis, a);
				const bIsCenter = this.isCenterDataPoint(axis, b);
				return aIsCenter ? -1 : bIsCenter ? 1 : 0;
			});
		}

		/**
		 * Returns the center data point for the given data point
		 * @param {wpd.Dataset} dataset - Dataset to get center data point for
		 * @param {wpd.DataPoint} dataPoint - Data point to get center data point for
		 * @returns {wpd.DataPoint|undefined} - Center data point for the given data
		 *   point, or undefined when the point has no tuple to look it up by
		 */
		#getCenterDataPoint(dataset, dataPoint) {
			const axis = wpd.appData.getPlotData().getAxesForDataset(dataset);

			if (this.isCenterDataPoint(axis, dataPoint)) {
				return dataPoint;
			}

			// non-center data points only have one tuple direction
			const tupleDirection = Object.keys(dataPoint.metadata.tuple ?? {})[0];
			const tupleId = dataPoint.metadata.tuple?.[tupleDirection]?.id;
			if (!tupleId) {
				return;
			}

			return dataset
				.getAllPixels()
				.find((dp) => dp.metadata.tuple?.[tupleDirection]?.id === tupleId);
		}

		/**
		 * Syncs the tuple data points for the given data point to the center data point
		 * @param {wpd.Dataset} dataset - Dataset to sync tuple data points for
		 * @param {wpd.DataPoint} dataPoint - Data point to sync tuple data points for
		 */
		/**
		 * Checks if the given tuple data would collide with another center point's tuple ID
		 * @param {wpd.Dataset} dataset - Dataset to check for collisions
		 * @param {Object} tupleData - Tuple data to check (from generateTupleData)
		 * @param {wpd.DataPoint} [excludeDataPoint] - Data point to exclude from collision check (the point being added/moved)
		 * @returns {Boolean} - True if a collision exists
		 */
		hasTupleIdCollision(dataset, tupleData, excludeDataPoint) {
			if (!tupleData) return false;

			const axis = wpd.appData.getPlotData().getAxesForDataset(dataset);
			const tupleIds = Object.values(tupleData)
				.map((datum) => datum?.id)
				.filter(Boolean);

			if (!tupleIds.length) return false;

			return dataset.getAllPixels().some((dp) => {
				if (dp === excludeDataPoint) return false;
				if (!this.isCenterDataPoint(axis, dp)) return false;
				if (!dp.metadata?.tuple) return false;

				return tupleIds.some((id) =>
					Object.values(dp.metadata.tuple).some((datum) => datum?.id === id),
				);
			});
		}

		/**
		 * Syncs the tuple data points for the given data point to the center data point
		 * @param {wpd.Dataset} dataset - Dataset to sync tuple data points for
		 * @param {wpd.DataPoint} dataPoint - Data point to sync tuple data points for
		 */
		syncTupleIdsToCenterPoint(dataset, dataPoint) {
			const centerPoint = this.#getCenterDataPoint(dataset, dataPoint);
			if (!centerPoint) {
				return;
			}

			const newTupleData = this.generateTupleData(
				dataset,
				centerPoint,
				"center",
			);

			// If no id could be minted, leave the existing tuples alone: a stale id
			// still saves, an id-less side does not. (DCTL-2561)
			if (!newTupleData) {
				return;
			}

			// Get all tuple data point indexes for the center data point
			const tupleDataPointIndexes = wpd.pointGroups.getTupleDataPointIndexes(
				dataset,
				dataPoint,
			);

			tupleDataPointIndexes.forEach((index) => {
				const tupleDataPoint = dataset.getPixel(index);
				const tupleData = tupleDataPoint.metadata.tuple;
				if (tupleData) {
					// for the center point, we can replace the tuple data wholesale
					// for other points, we need to update only the id so that we don't lose the tagUuid
					if (tupleDataPoint.metadata.pointGroupAxisDirection === "center") {
						tupleDataPoint.metadata.tuple = newTupleData;
					} else {
						if (tupleData.y && newTupleData.y?.id) {
							tupleData.y.id = newTupleData.y.id;
						}
						if (tupleData.x && newTupleData.x?.id) {
							tupleData.x.id = newTupleData.x.id;
						}
					}
				}
			});
		}
	}

	wpd.pointGroups = new PointGroups();
}

export { init };
